import { useEffect, useMemo, useState } from 'react';

import isEqual from 'lodash/isEqual';

import type {
  LensAggregate,
  LensCard,
  LensCardPeriod,
  LensCardType,
  LensCardView,
  LensColumnKind,
  LensDataset,
} from '~/types/leLens';

import { type LensGrain, grainExpression, parseGrain } from '../lensGrain';
import { ratioOfSumsFault } from '../ratioGuard';

import { declaredColumnNames, deriveDatasetFields, isDeclaredColumn } from './deriveFields';
import { type PanelField, SHELF_LABELS, type ShelfKey, shelvesForCardType } from './panelTypes';

/** The shelf a card opens on: the first (and for a metric or table, only) one it offers. */
function defaultTarget(type: LensCardType | undefined): ShelfKey {
  if (type === 'table') {
    return 'values';
  }
  if (type === 'metric') {
    return 'value';
  }
  return 'x';
}

/** The shelves a card fills in turn once one is assigned: the next empty one becomes the target. */
function shelfOrder(type: LensCardType | undefined): Array<ShelfKey> {
  if (type === 'metric') {
    return ['value'];
  }
  if (type === 'table') {
    return ['x', 'series', 'values'];
  }
  return ['x', 'value', 'series'];
}

/** The explicit table Columns selection replaces hidden single-measure/ratio bindings. */
function withSelectedColumns(view: LensCardView, values: Array<string>, type: LensCardType): LensCardView {
  const next = { ...view, values };
  if (type === 'table') {
    delete next.value;
    delete next.ratio;
  }
  return next;
}

export type PanelDraft = { datasetId: string | undefined; view: LensCardView; period?: LensCardPeriod };

function toDraft(card: LensCard | null): PanelDraft {
  return { datasetId: card?.datasetId, view: card?.view ? { ...card.view } : {}, period: card?.period };
}

export type UsePanelStateArgs = {
  card: LensCard | null;
  datasets: ReadonlyArray<LensDataset>;
  /** Columns the active card just returned. Used only until a saved custom dataset is declared. */
  runtimeColumns?: ReadonlyArray<string>;
  /** Fired on every shelf, dataset or where change: the live-preview hand-off. */
  onDraftChange?: (draft: PanelDraft) => void;
};

/**
 * All state and pure logic behind the Data tab: which shelf is targeted,
 * assigning and clearing fields, switching datasets, and what to say when a
 * shelf can no longer resolve. Kept apart from rendering so F1 and F2's rules
 * (targeting, refusal wording, dataset-switch clearing) are exercised without
 * mounting MUI at all.
 */
export function usePanelState({ card, datasets, onDraftChange, runtimeColumns = [] }: UsePanelStateArgs) {
  const [draft, setDraft] = useState<PanelDraft>(() => toDraft(card));
  const [target, setTarget] = useState<ShelfKey>(defaultTarget(card?.type));
  const [search, setSearch] = useState('');
  const [clearedNote, setClearedNote] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);

  // Re-seeds only when a different card opens: re-seeding on every render
  // (the lens object refetches on an interval) would drop an edit in
  // progress, the same trap the dialog's own effect already avoids today.
  const cardId = card?.id ?? null;
  useEffect(() => {
    setDraft(toDraft(card));
    setClearedNote(null);
    setRefusal(null);
    setSearch('');
    setTarget(defaultTarget(card?.type));
    // card and cardId are read together; cardId alone is the re-seed key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardId]);

  const savedDraftKey = JSON.stringify(toDraft(card));
  useEffect(() => {
    const saved = JSON.parse(savedDraftKey) as PanelDraft;
    setDraft((current) => (isEqual(JSON.parse(JSON.stringify(current)), saved) ? current : saved));
  }, [savedDraftKey]);

  useEffect(() => {
    onDraftChange?.(draft);
    // onDraftChange is a hand-off, not a dependency: a caller passing a new
    // closure each render must not re-fire the preview for no state change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);

  // Any target change makes a refusal tied to the old shelf stale. A refusal
  // set by this same tick's assign() is not affected: an assign() refusal
  // never changes the target, only a successful one does.
  useEffect(() => {
    setRefusal(null);
  }, [target]);

  const dataset = datasets.find((entry) => entry.id === draft.datasetId);
  const cardType: LensCardType = card?.type ?? 'bar';
  const shelfKeys = useMemo(() => shelvesForCardType(cardType), [cardType]);
  useEffect(() => {
    if (!shelfKeys.includes(target)) setTarget(defaultTarget(cardType));
  }, [cardType, shelfKeys, target]);
  const fields = useMemo(() => deriveDatasetFields(dataset), [dataset]);
  // For ratioOfSumsFault: the dataset's own declared names and kinds, which the
  // legacy Data tab never had (it only ever saw a query's returned column
  // names), so the panel's numerator/denominator suggestion is not a
  // regression of that guard, an improvement on it.
  const columnNames = useMemo(() => fields.map((field) => field.name), [fields]);
  const columnKinds = useMemo(
    () => Object.fromEntries(fields.map((field) => [field.name, field.kind])) as Record<string, LensColumnKind>,
    [fields],
  );
  // A custom dataset can already be serving a card while its dry-run schema has
  // not yet been persisted. Its current result is evidence for the fields this
  // card uses, but it is not a substitute for metadata once that metadata exists.
  const runtimeColumnNames = useMemo(
    () => new Set(runtimeColumns.map((column) => column.toLowerCase())),
    [runtimeColumns],
  );
  const hasDeclaredColumns = Boolean(dataset?.columns?.length);
  // A saved Group by can be a grain expression ("week(transaction_date)") rather than a bare
  // column name; only the column inside it is ever declared, so the grain wrapper has to be
  // stripped before checking, or a perfectly valid saved value paints red (a defect: the shelf
  // showed an error border and offered its "fix this" tooltip for a value that was never wrong).
  const isKnownColumn = (value: string | undefined): boolean => {
    if (!value) {
      return true;
    }
    const resolved = parseGrain(value)?.column ?? value;
    return (
      isDeclaredColumn(dataset, resolved) || (!hasDeclaredColumns && runtimeColumnNames.has(resolved.toLowerCase()))
    );
  };

  const shelfValues = (key: ShelfKey): Array<string> => {
    if (key === 'values') {
      return draft.view.values ?? [];
    }
    const value = draft.view[key];
    return typeof value === 'string' && value ? [value] : [];
  };

  const shelfBad = (key: ShelfKey): boolean => shelfValues(key).some((value) => !isKnownColumn(value));

  const shelfFixTooltip = (key: ShelfKey): string | undefined => {
    const bad = shelfValues(key).find((value) => !isKnownColumn(value));
    if (!bad) {
      return undefined;
    }
    return `${bad} is not returned by ${dataset?.name ?? 'this dataset'}, pick another or add it back.`;
  };

  const hasInvalidShelf = shelfKeys.some(shelfBad);

  /** Click a shelf then a field (F1), or drop a field straight onto one. Refuses a kind the shelf cannot hold. */
  function assignTo(key: ShelfKey, field: PanelField): void {
    setClearedNote(null);
    const isMeasureField = field.kind === 'measure' || field.kind === 'calculated';
    if (key === 'value' && !isMeasureField) {
      setRefusal(`Measure needs a number. ${field.name} is a ${field.kind === 'date' ? 'date' : 'dimension'}.`);
      return;
    }
    if ((key === 'x' || key === 'series') && isMeasureField) {
      setRefusal(`${field.name} is a measure. ${SHELF_LABELS[key]} takes dates and dimensions.`);
      return;
    }
    // A governed ratio measure names its own two parts; there is no aggregate to pick and no
    // ratio-of-sums fault to check, since the ratio is already the one the catalogue declared.
    if (key === 'value' && field.ratio) {
      setRefusal(null);
      setDraft((current) => {
        const nextView: LensCardView = { ...current.view, ratio: field.ratio };
        delete nextView.value;
        delete nextView.aggregate;
        return { ...current, view: nextView };
      });
      const remaining = shelfOrder(cardType).find((entry) => entry !== key && shelfValues(entry).length === 0);
      if (remaining) {
        setTarget(remaining);
      }
      return;
    }
    if (key === 'value') {
      // A calculated field lands on Sum (below); a governed measure defaults to the aggregate
      // the catalogue declares (its only honest choice for a countDistinct measure); a plain
      // measure keeps whatever aggregate the shelf already carries, defaulting to Sum, same as
      // an unset view reads at save and draw time.
      const aggregate = field.kind === 'calculated' ? 'sum' : (field.aggregate ?? draft.view.aggregate ?? 'sum');
      const fault = ratioOfSumsFault(field.name, aggregate, columnNames, columnKinds);
      if (fault) {
        setRefusal(fault.message);
        return;
      }
    }
    setRefusal(null);
    setDraft((current) => {
      if (key === 'values') {
        const existing = current.view.values ?? [];
        const values = existing.includes(field.name) ? existing : [...existing, field.name];
        return { ...current, view: withSelectedColumns(current.view, values, cardType) };
      }
      const nextView: LensCardView = { ...current.view, [key]: field.name };
      if (key === 'value') {
        // Dropping a plain measure onto Measure replaces whatever ratio was there before.
        delete nextView.ratio;
        if (field.kind === 'calculated') {
          nextView.aggregate = 'sum';
        } else if (field.aggregate) {
          nextView.aggregate = field.aggregate;
        }
      }
      return { ...current, view: nextView };
    });
    if (key !== 'values') {
      const remaining = shelfOrder(cardType).find(
        (entry) => entry !== key && !(entry === 'value' && draft.view.ratio) && shelfValues(entry).length === 0,
      );
      if (remaining) {
        setTarget(remaining);
      }
    }
  }

  /** F1 step 3: assigns to whichever shelf is currently the click target. */
  function assign(field: PanelField): void {
    assignTo(target, field);
  }

  function clear(key: ShelfKey): void {
    setClearedNote(null);
    setRefusal(null);
    setDraft((current) => {
      if (key === 'values') {
        return { ...current, view: withSelectedColumns(current.view, [], cardType) };
      }
      const nextView = { ...current.view };
      delete nextView[key];
      if (key === 'value') {
        delete nextView.ratio;
      }
      return { ...current, view: nextView };
    });
  }

  function clearValueAt(name: string): void {
    setDraft((current) => ({
      ...current,
      view: withSelectedColumns(
        current.view,
        (current.view.values ?? []).filter((value) => value !== name),
        cardType,
      ),
    }));
  }

  function setAggregate(aggregate: LensAggregate): void {
    const measure = draft.view.value;
    if (measure) {
      const fault = ratioOfSumsFault(measure, aggregate, columnNames, columnKinds);
      if (fault) {
        setRefusal(fault.message);
        return;
      }
    }
    setRefusal(null);
    setDraft((current) => ({ ...current, view: { ...current.view, aggregate } }));
  }

  /** The shelf chip's own grain menu (Day/Week/Month/Quarter): rewrites this shelf's saved
   *  string in place, keeping the same underlying column. Day stores the bare column name (the
   *  representation every date field already carries before anyone picks a grain), the coarser
   *  grains wrap it with `grainExpression`. */
  function setGrain(key: ShelfKey, grain: LensGrain): void {
    if (key === 'values') {
      return;
    }
    const current = draft.view[key];
    if (typeof current !== 'string' || !current) {
      return;
    }
    const column = parseGrain(current)?.column ?? current;
    const nextValue = grain === 'day' ? column : grainExpression(grain, column);
    setDraft((draftState) => ({ ...draftState, view: { ...draftState.view, [key]: nextValue } }));
  }

  function setWhere(column: string, values: Array<string>, exclude: boolean): void {
    setDraft((current) => ({
      ...current,
      view: {
        ...current.view,
        where: [
          ...(current.view.where ?? []).filter((clause) => clause.column !== column),
          { column, ...(exclude ? { notIn: values } : { in: values }) },
        ],
      },
    }));
  }

  function removeWhere(column: string): void {
    setDraft((current) => ({
      ...current,
      view: { ...current.view, where: (current.view.where ?? []).filter((clause) => clause.column !== column) },
    }));
  }

  /** F2: shelves that resolve by name on the new dataset stay; the rest are cleared and named. */
  function switchDataset(datasetId: string): void {
    if (datasetId === draft.datasetId) {
      return;
    }
    const nextDataset = datasets.find((entry) => entry.id === datasetId);
    const known = declaredColumnNames(nextDataset);
    const notes: Array<string> = [];
    const view: LensCardView = { ...draft.view };
    (['x', 'series', 'value'] as const).forEach((key) => {
      const value = view[key];
      if (value && !known.has(value.toLowerCase())) {
        notes.push(`${SHELF_LABELS[key]} cleared: ${nextDataset?.name ?? datasetId} has no ${value}`);
        delete view[key];
      }
    });
    if (view.values?.length) {
      const kept = view.values.filter((value) => known.has(value.toLowerCase()));
      if (kept.length !== view.values.length) {
        notes.push(`${SHELF_LABELS.values} cleared: ${nextDataset?.name ?? datasetId} has no matching column`);
      }
      view.values = kept;
    }
    if (view.where?.length) {
      view.where = view.where.filter((clause) => known.has(clause.column.toLowerCase()));
    }
    setRefusal(null);
    setDraft({ datasetId, view });
    setClearedNote(notes.length ? `${notes.join('. ')}. Preview redraws from rows already loaded.` : null);
    setTarget(view.x ? 'value' : 'x');
  }

  function resetDraft(): void {
    setDraft(toDraft(card));
    setClearedNote(null);
    setRefusal(null);
  }

  function setPeriod(period: LensCardPeriod | undefined) {
    setDraft((current) => ({ ...current, period }));
  }

  function buildNextCard(): LensCard | null {
    if (!card) {
      return null;
    }
    const { period: _period, ...rest } = card;
    return {
      ...rest,
      datasetId: draft.datasetId,
      view: draft.view,
      ...(draft.period !== undefined ? { period: draft.period } : {}),
    };
  }

  // New cards begin as a metric, so never persist its empty default shell.
  const hasRequiredFields = cardType !== 'metric' || Boolean(draft.view.value) || Boolean(draft.view.ratio);
  const hasSaveableSource = Boolean(draft.datasetId);
  const canSave = hasSaveableSource && hasRequiredFields && !hasInvalidShelf;
  // A dataset picked but never dry-run or declared: Data and Filters both
  // derive their fields from `fields` above, so both go empty at once. The
  // panel shell and the Data/Filters tabs all read this one flag rather than
  // each re-deriving it from `dataset`/`fields` themselves.
  const isUndeclared = Boolean(dataset) && fields.length === 0 && !dataset?.columns?.length;

  return {
    assign,
    assignTo,
    buildNextCard,
    canSave,
    clear,
    clearedNote,
    clearValueAt,
    dataset,
    draft,
    fields,
    hasInvalidShelf,
    isUndeclared,
    refusal,
    removeWhere,
    setPeriod,
    setWhere,
    resetDraft,
    search,
    setAggregate,
    setGrain,
    setSearch,
    setTarget,
    shelfBad,
    shelfFixTooltip,
    shelfKeys,
    shelfValues,
    switchDataset,
    target,
  };
}

export type PanelState = ReturnType<typeof usePanelState>;
